import { Box, BoxProps } from "@chakra-ui/react"
import { motion } from "framer-motion"
import { useLayoutEffect, useRef } from "react"

// Autofocus the first input _after_ animation completes.
// This avoids a situation where browser auto-complete suggestions
// (lke in Safari) get positioned in relation to the focused input's
// initial position versus the input's final position at the end of
// the animation.
const autoFocusNextElement = (element?: HTMLDivElement | null) => {
  // Only focus first input if no other element has focus.
  if (!document.hasFocus() || document.activeElement?.tagName === "BODY") {
    // NOTE: ignoring focusing checkbox inputs, as it seems slightly odd UX to do so.
    const inputs = element?.querySelectorAll(`
      input:not([readonly]):not([type='checkbox'])
    `) as HTMLInputElement[] | undefined

    // Attempt to find first input with no value.
    const input = [...(inputs || [])].find((input) => input.value === "")
    if (input) {
      input.focus()
    } else if (inputs?.length) {
      // Otherwise, focus first input.
      inputs[0].focus()
    }
  }
}

const AuthMotion = (props: BoxProps) => {
  const boxRef = useRef<HTMLDivElement | null>(null)
  const onAnimationComplete = () => autoFocusNextElement(boxRef.current)

  // In test environments, we don't wait for the animation to complete
  // as doing so seems to produce flaky tests.
  useLayoutEffect(() => {
    if (process.env.NODE_ENV === "test") {
      const timout = setTimeout(() => {
        onAnimationComplete()
      }, 0)
      return () => clearTimeout(timout)
    }
  }, [])

  return (
    <motion.div
      style={{ width: "100%" }}
      initial={{ opacity: 0, y: -30 }}
      animate={{
        opacity: 1,
        y: 0
      }}
      onAnimationComplete={onAnimationComplete}
    >
      <Box w="100%" ref={boxRef} {...props} />
    </motion.div>
  )
}

export default AuthMotion
